import type { FC } from 'react'

import { useId, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { SectionHeading } from '@/components/landing/section-heading'
import { faqs } from '@/constants/content'

export const Faq: FC = () => {
    const id = useId()
    const [open, setOpen] = useState<number | null>(0)

    return (
        <section id='faq' aria-labelledby='faq-title' className='relative z-10 scroll-mt-16 bg-background py-24 sm:py-32'>
            <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.4fr] lg:gap-20'>
                <div className='lg:sticky lg:top-28 lg:self-start'>
                    <SectionHeading
                        id='faq-title'
                        eyebrow="FAQ"
                        title="Questions, answered."
                        body="Charging, delivery, warranty and the rest. If yours is not here, ask it in your quote request."
                    />
                </div>

                <Reveal>
                    <div className='divide-y divide-white/10 border-y border-white/10'>
                        {faqs.map((item, index) => {
                            const expanded = open === index
                            const buttonId = `${id}-question-${index}`
                            const panelId = `${id}-answer-${index}`

                            return (
                                <div key={item.question}>
                                    <h3>
                                        <button
                                            id={buttonId}
                                            type='button'
                                            aria-expanded={expanded}
                                            aria-controls={panelId}
                                            onClick={() => setOpen(expanded ? null : index)}
                                            className='group flex w-full items-center justify-between gap-6 py-6 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
                                        >
                                            <span className='text-base font-semibold text-white transition-colors group-hover:text-brand sm:text-lg'>
                                                {item.question}
                                            </span>
                                            <span
                                                aria-hidden='true'
                                                className={cn(
                                                    'flex size-9 shrink-0 items-center justify-center rounded-full border transition-[transform,background-color,border-color] duration-300',
                                                    expanded ? 'rotate-45 border-brand bg-brand text-brand-foreground' : 'border-white/15 text-white'
                                                )}
                                            >
                                                <Plus className='size-4' />
                                            </span>
                                        </button>
                                    </h3>
                                    <div
                                        id={panelId}
                                        role='region'
                                        aria-labelledby={buttonId}
                                        inert={!expanded}
                                        className={cn(
                                            'grid transition-[grid-template-rows,opacity] duration-400 ease-[cubic-bezier(0.22,1,0.36,1)]',
                                            expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                                        )}
                                    >
                                        <div className='overflow-hidden'>
                                            <p className='max-w-2xl pb-6 pr-12 text-sm leading-relaxed text-white/65 sm:text-base'>
                                                {item.answer}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
